<script setup>
import { computed, ref } from 'vue';

/**
 * Narrow the comparison to specific packages.
 *
 * Options come from the UNFILTERED window, grouped the way the dashboard groups everything else,
 * so the list stays stable as selections change and only offers packages that actually sold -
 * there is nothing to compare on a package with no rows in either window.
 */
const props = defineProps({
  /** Product-line buckets from the unfiltered overview, each carrying its packages. */
  productLines: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);
const search = ref('');

const groups = computed(() => props.productLines
  .map(line => ({
    key: line.key,
    label: line.label,
    packages: line.packages
      .filter(pkg => pkg.name.toLowerCase().includes(search.value.trim().toLowerCase()))
      .sort((a, b) => b.revenue - a.revenue),
  }))
  .filter(group => group.packages.length > 0));

const selectedNames = computed(() => {
  const byId = new Map();
  props.productLines.forEach(line => line.packages.forEach(pkg => byId.set(pkg.packageId, pkg.name)));
  return props.modelValue.map(id => ({ id, name: byId.get(id) ?? `Package ${id}` }));
});

/**
 * Add or remove one package.
 * @param {number} packageId Package id.
 */
function toggle(packageId) {
  const next = props.modelValue.includes(packageId)
    ? props.modelValue.filter(id => id !== packageId)
    : [...props.modelValue, packageId];
  emit('update:modelValue', next);
}

/** Back to the whole business. */
function clearAll() {
  emit('update:modelValue', []);
}

/**
 * Select every package in one product line, so "compare Food Handler" is one click.
 * @param {object} group Group.
 */
function selectGroup(group) {
  const ids = group.packages.map(pkg => pkg.packageId);
  const merged = [...new Set([...props.modelValue, ...ids])];
  emit('update:modelValue', merged);
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
</script>

<template>
  <div :class="$style.wrapper">
    <div :class="$style.bar">
      <button type="button" :class="$style.toggle" @click="isOpen = !isOpen">
        <span aria-hidden="true" :class="$style.caret">{{ isOpen ? '▾' : '▸' }}</span>
        {{ modelValue.length === 0 ? 'All packages' : `${modelValue.length} package${modelValue.length === 1 ? '' : 's'}` }}
      </button>

      <div v-if="selectedNames.length" :class="$style.chips">
        <button
          v-for="item in selectedNames"
          :key="item.id"
          type="button"
          :class="$style.chip"
          :title="`Remove ${item.name}`"
          @click="toggle(item.id)"
        >
          {{ item.name }}<span aria-hidden="true" :class="$style.remove">×</span>
        </button>
        <button type="button" :class="$style.clear" @click="clearAll">
          Clear
        </button>
      </div>
    </div>

    <div v-if="isOpen" :class="$style.panel">
      <input
        v-model="search"
        type="search"
        placeholder="Search packages…"
        :class="$style.search"
      >

      <p v-if="groups.length === 0" :class="$style.empty">
        No packages sold in this window.
      </p>

      <div v-for="group in groups" :key="group.key" :class="$style.group">
        <div :class="$style.groupHead">
          <span :class="$style.groupLabel">{{ group.label }}</span>
          <button type="button" :class="$style.selectAll" @click="selectGroup(group)">
            Select all
          </button>
        </div>
        <label
          v-for="pkg in group.packages"
          :key="pkg.packageId"
          :class="$style.option"
        >
          <input
            type="checkbox"
            :checked="modelValue.includes(pkg.packageId)"
            @change="toggle(pkg.packageId)"
          >
          <span :class="$style.optionName">{{ pkg.name }}</span>
          <span :class="$style.optionValue">{{ money.format(pkg.revenue) }}</span>
        </label>
      </div>
    </div>

    <p :class="$style.note">
      <template v-if="modelValue.length === 0">
        Comparing the whole business. Pick packages to compare just those.
      </template>
      <template v-else>
        Revenue is the selected lines only. Order-level discount, tax and collected cash are not
        attributable to a single package, so they are left out while a filter is on.
      </template>
    </p>
  </div>
</template>

<style module>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.toggle {
  border: 1px solid var(--border-color);
  background: white;
  border-radius: var(--border-radius);
  padding: 7px 14px;
  font: inherit;
  font-size: var(--font-size-lg);
  font-weight: 500;
  color: var(--primary-font-dark-color);
  cursor: pointer;
}
.toggle:hover {
  border-color: var(--pfs-red);
}
.caret {
  color: var(--faint-color);
  margin-right: 4px;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.chip {
  border: 1px solid var(--pfs-teal);
  background: white;
  color: var(--pfs-teal);
  border-radius: var(--border-radius-xxl);
  padding: 4px 10px;
  font: inherit;
  font-size: var(--font-size-md);
  cursor: pointer;
}
.remove {
  margin-left: 6px;
  font-weight: 700;
}
.clear {
  border: none;
  background: none;
  color: var(--button-red);
  font: inherit;
  font-size: var(--font-size-md);
  text-decoration: underline;
  cursor: pointer;
}
.panel {
  border: 1px solid var(--border-color-light);
  border-radius: var(--border-radius-md);
  background: white;
  padding: 14px;
  max-height: 320px;
  overflow-y: auto;
}
.search {
  width: 100%;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: 7px 10px;
  font: inherit;
  font-size: var(--font-size-lg);
  margin-bottom: 12px;
}
.group {
  margin-bottom: 14px;
}
.groupHead {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--border-color-light);
  margin-bottom: 6px;
}
.groupLabel {
  font-size: var(--font-size-md);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--faint-color);
}
.selectAll {
  border: none;
  background: none;
  color: var(--pfs-teal);
  font: inherit;
  font-size: var(--font-size-md);
  cursor: pointer;
  text-decoration: underline;
}
.option {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 9px;
  align-items: center;
  padding: 4px 0;
  font-size: var(--font-size-lg);
  cursor: pointer;
}
.optionName {
  color: var(--primary-font-color);
}
.optionValue {
  color: var(--faint-color);
  font-variant-numeric: tabular-nums;
  font-size: var(--font-size-md);
}
.empty, .note {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
</style>
